import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ImageWithFallback from '../assets/ImageWithFallback.png';
import VisibeenLogo from '../assets/VisibeenLogo.png';
import InstagramIcon from '../components/Dashboard/icons/InstagramIcon';
import FacebookIcon from '../components/icons/FacebookIcon';
import LinkedInIcon from '../components/icons/LinkedInIcon';
import YouTubeIcon from '../components/Dashboard/icons/YouTubeIcon';
import api from '../services/api';

const pricingPlansData = {
    monthly: [
        {
            name: 'Basic',
            price: '₹1,199',
            description: 'Perfect for small/local businesses just getting started.',
            features: [
                'Keyword Research & On-Page SEO',
                'Google Business Profile Setup',
                'Local Directory Submissions',
                'Monthly SEO Report'
            ],
            featured: false
        },
        {
            name: 'Advanced',
            price: '₹1,999',
            description: 'Best for businesses ready to scale traffic & visibility.',
            features: [
                'Everything in Basic, plus:',
                'Competitor keyword analysis',
                'High-quality backlink building',
                'Bi-weekly growth reports'
            ],
            featured: true
        },
        {
            name: 'Pro',
            price: '₹2,999',
            description: 'For brands that want to dominate search rankings.',
            features: [
                'Everything in Advanced, plus:',
                'Multi-location SEO strategy',
                'Content marketing campaigns',
                'Dedicated SEO manager'
            ],
            featured: false
        }
    ],
    yearly: [
        {
            name: 'Basic',
            price: '₹11,999',
            description: 'Perfect for small/local businesses just getting started.',
            features: [
                'Keyword Research & On-Page SEO',
                'Google Business Profile Setup',
                'Local Directory Submissions',
                'Monthly SEO Report'
            ],
            featured: false
        },
        {
            name: 'Advanced',
            price: '₹19,999',
            description: 'Best for businesses ready to scale traffic & visibility.',
            features: [
                'Everything in Basic, plus:',
                'Competitor keyword analysis',
                'High-quality backlink building',
                'Bi-weekly growth reports'
            ],
            featured: true
        },
        {
            name: 'Pro',
            price: '₹29,999',
            description: 'For brands that want to dominate search rankings.',
            features: [
                'Everything in Advanced, plus:',
                'Multi-location SEO strategy',
                'Content marketing campaigns',
                'Dedicated SEO manager'
            ],
            featured: false
        }
    ]
};

const AdsPage = () => {
    const navigate = useNavigate();
    const [activeFAQ, setActiveFAQ] = useState(null);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [showContactModal, setShowContactModal] = useState(false);
    const [showAboutModal, setShowAboutModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showDemoModal, setShowDemoModal] = useState(false);
    const [couponCopied, setCouponCopied] = useState(false);
    const [demoFormData, setDemoFormData] = useState({
        fullName: '',
        workEmail: '',
        phoneNumber: '',
        companyName: '',
        preferredDate: '',
        preferredTime: '',
        message: ''
    });
    const [demoSubmitting, setDemoSubmitting] = useState(false);
    const reviewsRef = useRef(null);

    const scrollReviews = (direction) => {
        if (reviewsRef.current) {
            const scrollAmount = 350;
            reviewsRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const toggleFAQ = (index) => {
        setActiveFAQ(activeFAQ === index ? null : index);
    };

    const handleDemoInputChange = (e) => {
        const { name, value } = e.target;
        setDemoFormData(prev => ({ ...prev, [name]: value }));
    };

    const [demoSuccess, setDemoSuccess] = useState(false);

    const handleLoginNavigation = async (e) => {
        if (e) e.preventDefault();
        const coupon = 'E2Evisibeen';
        try {
            await navigator.clipboard.writeText(coupon);
        } catch (err) {
            try {
                const textarea = document.createElement('textarea');
                textarea.value = coupon;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            } catch (err2) {
                // ignore
            }
        }

        toast.success(
            <div>
                <span style={{ fontWeight: 'bold' }}>Coupon copied!</span> <br />
                Paste on pricing page to get ₹1 trial.
            </div>,
            {
                position: "top-center",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored",
            }
        );

        setTimeout(() => {
            // Mark that login came from ads page - used by PlansPricing to show both monthly and yearly options
            try { localStorage.setItem('login_source', 'ads'); } catch (_) { }
            // Also set a sessionStorage marker to indicate this is a fresh navigation from ads
            // This prevents the marker from being cleared during login
            try { sessionStorage.setItem('login_source_fresh', 'ads'); } catch (_) { }
            navigate('/login');
        }, 2000);
    };

    const handleDemoSubmit = async (e) => {
        e.preventDefault();
        if (!demoFormData.fullName || !demoFormData.workEmail || !demoFormData.phoneNumber || !demoFormData.companyName) {
            alert('Please fill in all required fields.');
            return;
        }
        setDemoSubmitting(true);
        console.log(' Submitting demo request form...', demoFormData);
        try {
            const payload = {
                name: demoFormData.fullName,
                email: demoFormData.workEmail,
                phone_number: demoFormData.phoneNumber,
                business_name: demoFormData.companyName,
                complaint_type: 'Demo Request',
                date_and_time: demoFormData.preferredDate && demoFormData.preferredTime
                    ? `${demoFormData.preferredDate} ${demoFormData.preferredTime}`
                    : demoFormData.preferredDate || '',
                message: demoFormData.message || `Demo request from ${demoFormData.companyName}. Preferred time: ${demoFormData.preferredTime || 'Not specified'}`
            };
            console.log(' Sending demo request to API...', payload);
            const response = await api.post('/customer/contact-us/create-contact', payload, { noAuth: true, noAutoLogout: true });
            console.log(' Demo request submitted successfully!', response);
            setDemoSuccess(true);
            setDemoFormData({
                fullName: '',
                workEmail: '',
                phoneNumber: '',
                companyName: '',
                preferredDate: '',
                preferredTime: '',
                message: ''
            });
        } catch (error) {
            console.error(' Failed to submit demo request:', error);
            alert('Failed to submit demo request. Please try again or contact us.');
        } finally {
            setDemoSubmitting(false);
        }
    };

    return (
        <div style={{ margin: 0, padding: 0, fontFamily: 'Arial, sans-serif', background: '#ffffff', color: '#222' }}>

            {/* Responsive CSS */}
            <style>{`
                /* Desktop and large tablets */
                @media (min-width: 901px) {
                    .benefits-grid { grid-template-columns: repeat(2, 1fr) !important; }
                }

                /* Tablet and below - Single column for benefits */
                @media (max-width: 900px) {
                    .benefits-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
                }
                
                /* Tablet and below */
                @media (max-width: 768px) {
                    .nav-menu { display: none !important; }
                    .header-padding { padding: 12px 16px !important; }
                    .hero-section { padding: 80px 20px 60px !important; }
                    .hero-title { font-size: 36px !important; }
                    .section-padding { padding: 50px 16px !important; margin: 0 !important; }
                    .pricing-grid { grid-template-columns: 1fr !important; }
                    .benefit-card { transform: scale(1) !important; }
                    .section-title { font-size: 32px !important; }
                    .benefit-item { padding: 18px 16px !important; }
                    .outer { padding: 50px 20px !important; }
                    .title-1 { font-size: 32px !important; }
                    .title-2 { font-size: 28px !important; }
                    .grid-2 { grid-template-columns: 1fr !important; }
                    .solution-card-content { padding: 2rem !important; }
                    .solution-header h2 { font-size: 1.5rem !important; }
                    .problem-section { padding: 3rem 1rem !important; }
                    .problem-section__title { font-size: 2.5rem !important; }
                    .problem-section__grid { grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)) !important; gap: 1rem !important; }
                    /* Reviews carousel responsive - tablet */
                    .reviews-grid::-webkit-scrollbar {
                        display: none !important;
                    }
                    .reviews-carousel-container {
                        padding: 0 40px !important;
                    }
                    .reviews-nav-btn {
                        width: 40px !important;
                        height: 40px !important;
                    }
                    .review-card {
                        min-width: 280px !important;
                        flex: 0 0 280px !important;
                    }
                    /* Footer responsive */
                    .footer-container { 
                        flex-direction: column !important; 
                        gap: 28px !important; 
                        text-align: center !important;
                    }
                    .footer-left { 
                        align-items: center !important;
                        gap: 14px !important;
                    }
                    .footer-social { 
                        justify-content: center !important;
                        margin-top: 4px !important;
                    }
                    .footer-columns {
                        flex-wrap: wrap !important;
                        gap: 24px !important;
                    }
                    .footer-column {
                        min-width: 140px !important;
                        text-align: center !important;
                    }
                    .footer-newsletter {
                        max-width: 100% !important;
                        flex: 1 1 100% !important;
                        align-items: center !important;
                    }
                    .footer-newsletter h3,
                    .footer-newsletter p {
                        text-align: center !important;
                    }
                    .footer-newsletter-form {
                        flex-direction: column !important;
                        gap: 10px !important;
                        align-items: center !important;
                    }
                    .footer-newsletter-form input {
                        width: 100% !important;
                        min-width: unset !important;
                        text-align: center !important;
                    }
                    .footer-newsletter-form button {
                        width: 100% !important;
                    }
                    .footer-bottom {
                        flex-direction: column !important;
                        gap: 12px !important;
                        text-align: center !important;
                    }
                    .footer-bottom-links {
                        flex-wrap: wrap !important;
                        justify-content: center !important;
                        gap: 16px !important;
                    }
                }
                
                /* Mobile */
                @media (max-width: 480px) {
                    .sign-in-link { display: none !important; }
                    .hero-title { font-size: 28px !important; line-height: 1.3 !important; }
                    .cta-button { padding: 8px 14px !important; font-size: 13px !important; }
                    .hero-section { padding: 70px 12px 50px !important; }
                    .section-padding { padding: 40px 12px !important; margin: 0 !important; }
                    .section-title { font-size: 24px !important; line-height: 1.2 !important; }
                    .benefit-item { padding: 16px 12px !important; }
                    .benefits-grid { gap: 12px !important; }
                    .outer { padding: 40px 16px !important; }
                    .title-1 { font-size: 28px !important; }
                    .title-2 { font-size: 24px !important; }
                    .solution-card-content { padding: 1.5rem !important; }
                    .solution-header h2 { font-size: 1.25rem !important; }
                    .solution-features-grid { grid-template-columns: 1fr !important; gap: 1rem !important; }
                    .solution-feature-card { padding: 1rem !important; }
                    .solution-feature-card h3 { font-size: 1rem !important; }
                    .solution-feature-card p { font-size: 0.8rem !important; }
                    .solution-callout { padding: 1rem !important; font-size: 0.9rem !important; }
                    .solution-button-group { flex-direction: column !important; gap: 0.75rem !important; }
                    .solution-btn { padding: 0.75rem 1rem !important; font-size: 0.9rem !important; }
                    .problem-section { padding: 2rem 0.75rem !important; }
                    .problem-section__title { font-size: 2rem !important; margin-bottom: 2rem !important; }
                    .problem-section__grid { grid-template-columns: 1fr !important; gap: 1rem !important; }
                    .problem-section__card { padding: 1rem !important; }
                    .problem-section__card-title { font-size: 1rem !important; }
                    .problem-section__card-description { font-size: 0.85rem !important; }
                    .problem-section__icon { width: 2.5rem !important; height: 2.5rem !important; font-size: 1.25rem !important; }
                    .problem-section__cta-text { font-size: 1rem !important; }
                    .problem-section__cta-button { padding: 0.75rem 1.5rem !important; font-size: 0.9rem !important; }
                    /* Reviews grid mobile responsive */
                    .reviews-grid { 
                        gap: 12px !important;
                    }
                    .reviews-grid::-webkit-scrollbar {
                        display: none !important;
                    }
                    .reviews-carousel-container {
                        margin-top: 25px !important;
                        padding: 0 35px !important;
                    }
                    .reviews-nav-btn {
                        width: 32px !important;
                        height: 32px !important;
                    }
                    .reviews-nav-btn-left {
                        left: 0px !important;
                    }
                    .reviews-nav-btn-right {
                        right: 0px !important;
                    }
                    .reviews-nav-btn span {
                        font-size: 14px !important;
                    }
                    .review-card {
                        min-width: 260px !important;
                        flex: 0 0 260px !important;
                        padding: 18px !important;
                        min-height: 260px !important;
                    }
                    .review-card p {
                        font-size: 16px !important;
                    }
                    .review-card div {
                        font-size: 13px !important;
                    }
                    /* Footer mobile responsive */
                    .footer-main-content {
                        padding: 24px 16px 16px !important;
                    }
                    .footer-container { 
                        gap: 10px !important; 
                        margin-bottom: 12px !important;
                    }
                    .footer-left {
                        gap: 8px !important;
                    }
                    .footer-social {
                        margin-top: 0 !important;
                        margin-bottom: 0 !important;
                    }
                    .footer-columns {
                        flex-direction: column !important;
                        gap: 18px !important;
                    }
                    .footer-column {
                        min-width: 100% !important;
                    }
                    .footer-column ul {
                        gap: 10px !important;
                    }
                    .footer-newsletter {
                        flex: 1 1 100% !important;
                        max-width: 100% !important;
                    }
                    .footer-bottom {
                        flex-direction: column !important;
                        gap: 10px !important;
                        padding: 16px !important;
                    }
                    .footer-bottom-links {
                        justify-content: center !important;
                        gap: 16px !important;
                    }
                }

                /* Small mobile */
                @media (max-width: 360px) {
                    .hero-title { font-size: 24px !important; }
                    .title-1 { font-size: 24px !important; }
                    .title-2 { font-size: 20px !important; }
                    .solution-card-content { padding: 1rem !important; }
                    .solution-header h2 { font-size: 1.125rem !important; }
                    .solution-feature-card { padding: 0.75rem !important; }
                    .problem-section__title { font-size: 1.75rem !important; }
                    .problem-section__card { padding: 0.75rem !important; }
                }

                /* Ensure full width on all devices */
                * { box-sizing: border-box; }
                body { margin: 0; padding: 0; overflow-x: hidden; }
                section { max-width: 100vw; overflow-x: hidden; }

                .hero-avatars span:first-child { margin-left: 0; }

                .hero-metrics p {
                    font-size: 13px;
                    color: #475467;
                    line-height: 1.4;
                }

                .hero-offer {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    margin-top: 6px;
                    padding: 12px 20px;
                    border-radius: 12px;
                    background: linear-gradient(120deg, #ff6b32, #ff9068);
                    color: white;
                    font-weight: 600;
                    box-shadow: 0 18px 30px rgba(255, 107, 50, 0.35);
                }

                .hero-visual {
                    flex: 1;
                    display: flex;
                    justify-content: center;
                }

                .hero-visual-card {
                    position: relative;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 30px 80px rgba(15, 23, 42, 0.2);
                    background: white;
                    width: 100%;
                    max-width: 520px;
                }

                .hero-score-card {
                    position: absolute;
                    top: 24px;
                    right: 24px;
                    background: white;
                    border-radius: 16px;
                    padding: 16px;
                    text-align: center;
                    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
                }

                .hero-score-card h4 {
                    margin: 0;
                    font-size: 32px;
                    color: #10b981;
                }

                .hero-stats {
                    position: absolute;
                    bottom: 16px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: calc(100% - 48px);
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 12px;
                    background: white;
                    border-radius: 16px;
                    padding: 18px;
                    box-shadow: 0 20px 45px rgba(15, 23, 42, 0.12);
                }

                .hero-stats div {
                    text-align: center;
                }

                .hero-stats h5 {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 700;
                }

                .hero-stats p {
                    margin: 4px 0 0;
                    font-size: 12px;
                    color: #475467;
                }

                /* Problem section redesign */
                .problem-section {
                    width: 100%;
                    background: #ffffff;
                    text-align: center;
                    padding: 5rem 1.5rem;
                }

                .problem-section__container {
                    width: 100%;
                    max-width: 80rem;
                    margin: 0 auto;
                    padding: 0 1.5rem;
                }

                .problem-section__badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: #fef2f2;
                    color: #dc2626;
                    padding: 0.5rem 1.25rem;
                    border-radius: 9999px;
                    font-size: 0.875rem;
                    font-weight: 600;
                    margin-bottom: 1.5rem;
                    animation: fadeIn 0.5s ease;
                }

                .problem-section__title {
                    font-size: clamp(2rem, 8vw, 3.75rem);
                    font-weight: 700;
                    color: #111827;
                    line-height: 1.2;
                    margin-bottom: 4rem;
                    animation: fadeIn 0.5s ease 0.1s backwards;
                }

                .problem-section__highlight {
                    color: #ef4444;
                }

                .problem-section__grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 1.5rem;
                    margin-bottom: 3rem;
                }

                .problem-section__card {
                    background: #ffffff;
                    border: 2px solid #fee2e2;
                    border-radius: 1rem;
                    padding: 1.5rem;
                    box-shadow: 0 10px 15px -3px rgba(239, 68, 68, 0.1);
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    text-align: left;
                    animation: fadeIn 0.5s ease backwards;
                    position: relative;
                    overflow: hidden;
                }

                .problem-section__card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(135deg, rgba(239, 68, 68, 0.02) 0%, rgba(168, 85, 247, 0.02) 100%);
                    opacity: 0;
                    transition: opacity 0.4s ease;
                    border-radius: 1rem;
                }

                .problem-section__card:hover {
                    transform: translateY(-8px) scale(1.02);
                    box-shadow: 0 25px 35px -5px rgba(239, 68, 68, 0.2);
                    border-color: #f87171;
                    background: linear-gradient(135deg, #ffffff 0%, #fef7ff 100%);
                }

                .problem-section__card:hover::before {
                    opacity: 1;
                }

                .problem-section__card:hover .problem-section__icon {
                    background: linear-gradient(135deg, #ef4444 0%, #a855f7 100%);
                    transform: scale(1.1) rotate(5deg);
                    box-shadow: 0 8px 20px rgba(239, 68, 68, 0.3);
                }

                .problem-section__card:hover .problem-section__card-title {
                    color: #dc2626;
                    transform: translateX(4px);
                }

                .problem-section__card:hover .problem-section__card-description {
                    color: #374151;
                }

                .problem-section__card:nth-child(1) { animation-delay: 0.2s; }
                .problem-section__card:nth-child(2) { animation-delay: 0.3s; }
                .problem-section__card:nth-child(3) { animation-delay: 0.4s; }
                .problem-section__card:nth-child(4) { animation-delay: 0.5s; }

                .problem-section__card-header {
                    display: flex;
                    gap: 1rem;
                }

                .problem-section__icon {
                    flex-shrink: 0;
                    width: 3rem;
                    height: 3rem;
                    background: #fef2f2;
                    border-radius: 0.75rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #ef4444;
                    font-size: 1.5rem;
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .problem-section__card-title {
                    font-size: 1.125rem;
                    font-weight: 600;
                    color: #111827;
                    margin-bottom: 0.5rem;
                    transition: all 0.3s ease;
                }

                .problem-section__card-description {
                    font-size: 0.95rem;
                    color: #4b5563;
                    line-height: 1.6;
                    margin: 0;
                }

                .problem-section__cta {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 1.5rem;
                    animation: fadeIn 0.5s ease 0.6s backwards;
                }

                .problem-section__cta-text {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #9333ea;
                    margin: 0;
                }

                .problem-section__cta-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: linear-gradient(to right, #4f46e5, #a855f7);
                    color: #ffffff;
                    padding: 1rem 2rem;
                    border-radius: 0.75rem;
                    font-weight: 500;
                    font-size: 1rem;
                    border: none;
                    cursor: pointer;
                    text-decoration: none;
                    box-shadow: 0 10px 15px -3px rgba(168, 85, 247, 0.3);
                    transition: all 0.3s ease;
                }

                .problem-section__cta-button:hover {
                    box-shadow: 0 20px 25px -5px rgba(168, 85, 247, 0.4);
                    transform: scale(1.05);
                }

                /* Solution Section - New Design */
                .solution-card {
                    background: white;
                    border-radius: 1.5rem;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
                    border: 1px solid #e5e7eb;
                    overflow: hidden;
                }

                .solution-card-content {
                    padding: 3rem;
                }

                .solution-header {
                    margin-bottom: 2.5rem;
                }

                .solution-header h2 {
                    font-size: 2rem;
                    font-weight: 700;
                    color: #111827;
                    margin-bottom: 0.75rem;
                    line-height: 1.2;
                }

                .solution-header-accent {
                    height: 0.375rem;
                    width: 6rem;
                    background: linear-gradient(90deg, #2563eb, #7c3aed);
                    border-radius: 9999px;
                }

                .solution-features-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 1.25rem;
                    margin-bottom: 2rem;
                }

                .solution-feature-card {
                    position: relative;
                    background: linear-gradient(135deg, #f3f4f6 0%, #f9fafb 100%);
                    border: 1px solid rgba(229, 231, 235, 0.5);
                    border-radius: 1.125rem;
                    padding: 1.5rem;
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    cursor: pointer;
                    overflow: hidden;
                }

                .solution-feature-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(168, 85, 247, 0.08));
                    opacity: 0;
                    transition: opacity 0.4s ease;
                    border-radius: 1.125rem;
                    z-index: 1;
                }

                .solution-feature-card:hover {
                    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
                    transform: translateY(-12px) scale(1.03);
                    border-color: rgba(59, 130, 246, 0.3);
                    background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
                }

                .solution-feature-card:hover::before {
                    opacity: 1;
                }

                .solution-feature-card:hover .solution-feature-icon-wrapper {
                    transform: scale(1.15) rotate(8deg);
                    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
                    filter: brightness(1.1);
                }

                .solution-feature-card:hover h3 {
                    color: #1e40af;
                    transform: translateX(6px);
                    font-weight: 800;
                }

                .solution-feature-card:hover p {
                    color: #1f2937;
                    transform: translateX(3px);
                }

                .solution-feature-card:hover .solution-emerald {
                    background: linear-gradient(135deg, #059669, #0d9488);
                }

                .solution-feature-card:hover .solution-blue {
                    background: linear-gradient(135deg, #2563eb, #0891b2);
                }

                .solution-feature-card:hover .solution-violet {
                    background: linear-gradient(135deg, #7c3aed, #a855f7);
                }

                .solution-feature-card:hover .solution-orange {
                    background: linear-gradient(135deg, #ea580c, #dc2626);
                }

                .solution-feature-card:hover .solution-pink {
                    background: linear-gradient(135deg, #db2777, #e11d48);
                }

                .solution-feature-card:hover .solution-amber {
                    background: linear-gradient(135deg, #d97706, #ca8a04);
                }

                .solution-feature-icon-wrapper {
                    display: inline-flex;
                    padding: 0.75rem;
                    border-radius: 0.75rem;
                    margin-bottom: 1rem;
                    color: white;
                    transition: transform 0.3s ease;
                    width: fit-content;
                }

                .solution-feature-icon-wrapper svg {
                    width: 1.5rem;
                    height: 1.5rem;
                }

                .solution-emerald { background: linear-gradient(135deg, #10b981, #14b8a6); }
                .solution-blue { background: linear-gradient(135deg, #3b82f6, #06b6d4); }
                .solution-violet { background: linear-gradient(135deg, #8b5cf6, #a855f7); }
                .solution-orange { background: linear-gradient(135deg, #f97316, #ef4444); }
                .solution-pink { background: linear-gradient(135deg, #ec4899, #f43f5e); }
                .solution-amber { background: linear-gradient(135deg, #f59e0b, #eab308); }

                .solution-feature-card h3 {
                    font-size: 1.125rem;
                    font-weight: 700;
                    color: #111827;
                    margin-bottom: 0.75rem;
                    line-height: 1.4;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .solution-feature-card p {
                    font-size: 0.875rem;
                    color: #4b5563;
                    line-height: 1.625;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .solution-feature-card::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border-radius: 1.125rem;
                    background: linear-gradient(135deg, transparent, transparent, rgba(59, 130, 246, 0.05));
                    opacity: 0;
                    transition: opacity 0.3s ease;
                    pointer-events: none;
                }

                .solution-feature-card:hover::after {
                    opacity: 1;
                }

                .solution-callout {
                    background: linear-gradient(90deg, #fef3c7, #fef9e7);
                    border-left: 4px solid #f59e0b;
                    border-radius: 1.125rem;
                    padding: 1.5rem;
                    margin: 2rem 0;
                    display: flex;
                    gap: 0.75rem;
                }

                .solution-callout-icon {
                    font-size: 1.5rem;
                    flex-shrink: 0;
                    margin-top: 0.125rem;
                }

                .solution-callout p {
                    color: #111827;
                    font-weight: 600;
                    font-size: 1rem;
                    margin: 0;
                    line-height: 1.5;
                }

                .solution-callout-highlight {
                    color: #b45309;
                }

                .solution-button-group {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1rem;
                    margin-top: 2rem;
                }

                .solution-btn {
                    padding: 1rem 2rem;
                    border-radius: 0.75rem;
                    font-weight: 600;
                    font-size: 1rem;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    text-decoration: none;
                }

                .solution-btn-primary {
                    background: linear-gradient(135deg, #2563eb, #7c3aed);
                    color: white;
                    box-shadow: 0 10px 20px rgba(37, 99, 235, 0.3);
                }

                .solution-btn-primary:hover {
                    box-shadow: 0 15px 30px rgba(37, 99, 235, 0.4);
                    transform: scale(1.05);
                }

                .solution-btn-primary:active {
                    transform: scale(0.95);
                }

                .solution-btn-secondary {
                    background: white;
                    color: #111827;
                    border: 2px solid #d1d5db;
                }

                .solution-btn-secondary:hover {
                    background: #f9fafb;
                    transform: scale(1.05);
                }

                .solution-btn-secondary:active {
                    transform: scale(0.95);
                }

                .solution-btn-icon {
                    margin-right: 0.5rem;
                }

                @media (max-width: 640px) {
                    .solution-card-content {
                        padding: 1.5rem;
                    }

                    .solution-header h2 {
                        font-size: 1.5rem;
                    }

                    .solution-features-grid {
                        grid-template-columns: 1fr;
                    }

                    .solution-button-group {
                        flex-direction: column;
                    }

                    .solution-btn {
                        width: 100%;
                    }
                }

                @media (min-width: 641px) and (max-width: 768px) {
                    .solution-features-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (min-width: 769px) {
                    .solution-features-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>

            {/* HEADER */}
            <header style={{ width: '100%', background: '#ffffff', borderBottom: '1px solid #e5e7eb', position: 'fixed', top: 0, left: 0, zIndex: 1000, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div className="header-padding" style={{ width: '100%', padding: '16px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                    {/* Logo */}
                    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <img src={VisibeenLogo} alt="Visibeen Logo" style={{ height: '48px', maxWidth: '160px' }} />
                    </div>

                    {/* Navigation Menu - Hidden on mobile */}
                    <nav className="nav-menu" style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                        <a href="#features" style={{ color: '#374151', fontSize: '15px', fontWeight: 500, textDecoration: 'none', transition: 'color 0.2s ease' }}>
                            Features
                        </a>
                        <a href="#pricing" style={{ color: '#374151', fontSize: '15px', fontWeight: 500, textDecoration: 'none', transition: 'color 0.2s ease' }}>
                            Pricing
                        </a>
                        <a href="#faq" style={{ color: '#374151', fontSize: '15px', fontWeight: 500, textDecoration: 'none', transition: 'color 0.2s ease' }}>
                            FAQ
                        </a>
                    </nav>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                        <button
                            type="button"
                            onClick={async () => {
                                const coupon = 'E2Evisibeen';
                                try {
                                    await navigator.clipboard.writeText(coupon);
                                } catch (err) {
                                    try {
                                        const textarea = document.createElement('textarea');
                                        textarea.value = coupon;
                                        textarea.style.position = 'fixed';
                                        textarea.style.opacity = '0';
                                        document.body.appendChild(textarea);
                                        textarea.focus();
                                        textarea.select();
                                        document.execCommand('copy');
                                        document.body.removeChild(textarea);
                                    } catch (err2) {
                                        // ignore
                                    }
                                }
                                setCouponCopied(true);
                                window.setTimeout(() => setCouponCopied(false), 4000);
                            }}
                            style={{
                                padding: '8px 14px',
                                borderRadius: '8px',
                                border: '1px solid #3c5bff',
                                background: 'white',
                                color: '#3c5bff',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(60,91,255,0.12)',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {couponCopied ? '✓ Copied!' : '🎟️ Copy Coupon'}
                        </button>
                        <a href="/login" onClick={handleLoginNavigation} className="cta-button" style={{ background: '#3c5bff', color: 'white', padding: '8px 16px', fontSize: '14px', fontWeight: 600, borderRadius: '8px', textDecoration: 'none', display: 'inline-block', boxShadow: '0 2px 8px rgba(60,91,255,0.3)', transition: 'all 0.2s ease', whiteSpace: 'nowrap' }}>
                            Activate Trial in ₹1
                        </a>
                    </div>
                </div>
            </header>

            {/* HERO MAIN WRAPPER */}
            <section className="hero-section" style={{ width: '100%', backgroundImage: "url('Hero (1).png'), linear-gradient(135deg,#eef2ff 0%,#e8f5ff 40%,#f3e8ff 100%)", backgroundSize: 'cover, cover', backgroundPosition: 'center, center', padding: '100px 40px 80px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', margin: '0', marginTop: '60px' }}>

                {/* LEFT SIDE */}
                <div style={{ flex: 1, minWidth: '320px', maxWidth: '600px', padding: '20px' }}>

                    {/* Top badges */}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '25px' }}>
                        <div style={{ background: 'white', color: '#3c5bff', padding: '8px 14px', borderRadius: '20px', border: '1px solid #BEDBFF', fontSize: '13px', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
                            ⭐ AI does the digital work — you get the local visibility.
                        </div>
                        <div style={{ background: 'white', color: '#008236', padding: '8px 14px', borderRadius: '20px', border: '1px solid #B9F8CF', fontSize: '13px', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
                            📈 93% rank improvements
                        </div>
                    </div>

                    {/* Main Title */}
                    <h1 style={{ fontSize: '48px', lineHeight: 1.2, margin: 0, color: '#111' }}>
                        No Guesswork.<br />
                        No Stress.<br />
                        <span style={{ color: '#6a29ff' }}>Let AI</span> Automate Your Google Business Profile with Visibeen<br />
                    </h1>

                    {/* Subtitle */}
                    <p style={{ marginTop: '20px', fontSize: '17px', color: '#444', maxWidth: '520px', lineHeight: 1.6 }}>
                        With Visibeen, you don't just audit your local presence— you <span style={{ color: '#6a29ff', fontWeight: 'bold' }}>own it</span>.
                        One dashboard, one score, thousands of insights.
                    </p>

                    {/* CTA Buttons */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '30px', alignItems: 'center' }}>
                        <a href="#" onClick={handleLoginNavigation} style={{ background: '#3c5bff', color: 'white', padding: '14px 22px', fontSize: '15px', borderRadius: '10px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 10px rgba(60,91,255,0.3)' }}>
                            Check Score in just ₹1 →
                        </a>

                        <button
                            type="button"
                            onClick={async () => {
                                const coupon = 'E2Evisibeen';
                                try {
                                    await navigator.clipboard.writeText(coupon);
                                } catch (err) {
                                    try {
                                        const textarea = document.createElement('textarea');
                                        textarea.value = coupon;
                                        textarea.style.position = 'fixed';
                                        textarea.style.opacity = '0';
                                        document.body.appendChild(textarea);
                                        textarea.focus();
                                        textarea.select();
                                        document.execCommand('copy');
                                        document.body.removeChild(textarea);
                                    } catch (err2) {
                                        // ignore
                                    }
                                }
                                setCouponCopied(true);
                                window.setTimeout(() => setCouponCopied(false), 4000);
                            }}
                            style={{
                                padding: '14px 18px',
                                borderRadius: '10px',
                                border: '1px solid #3c5bff',
                                background: 'white',
                                color: '#3c5bff',
                                fontSize: '15px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                boxShadow: '0 4px 10px rgba(60,91,255,0.12)'
                            }}
                        >
                            {couponCopied ? 'Copied' : 'Copy coupon code'}
                        </button>

                        {couponCopied && (
                            <div style={{ fontSize: '16px', color: '#1f2937', fontWeight: 700 }}>
                                Coupon copied! Use this coupon code in pricing page to get trial.
                            </div>
                        )}
                    </div>

                    {/* Mini stats */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '25px' }}>
                        <div style={{ display: 'flex', gap: '-4px' }}>
                            <div style={{ background: '#3c5bff', color: 'white', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>JD</div>
                            <div style={{ background: '#8b4dff', color: 'white', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>SM</div>
                            <div style={{ background: '#ff4dc3', color: 'white', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>AL</div>
                            <div style={{ background: '#ff6b6b', color: 'white', width: '48px', height: '32px', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>+500</div>
                        </div>

                        <div style={{ fontSize: '12px', color: '#444', lineHeight: 1.4 }}>
                            <strong>10 minutes. 1 click.</strong><br />
                            Instantly know your local SEO health and how to beat your biggest competitor.
                        </div>
                    </div>


                </div>

                {/* RIGHT SIDE IMAGE CARD */}
                <div style={{ flex: 1, minWidth: '320px', maxWidth: '620px', padding: '20px', display: 'flex', justifyContent: 'center' }}>

                    <div style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', background: 'white' }}>

                        <img src={ImageWithFallback} alt="Laptop" style={{ width: '100%', display: 'block' }} />

                        {/* Score box */}
                        <div style={{ position: 'absolute', top: '20px', right: '20px', background: 'white', borderRadius: '12px', padding: '12px 16px', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                            <div style={{ fontSize: '28px', color: '#00a23a', fontWeight: 'bold' }}>836</div>
                            <div style={{ fontSize: '12px', color: '#333' }}>Visibeen Score</div>
                            <div style={{ fontSize: '11px', color: '#00a23a', marginTop: '4px' }}>📈 +127 this month</div>
                        </div>

                        {/* Bottom stats */}
                        <div style={{ position: 'absolute', bottom: '0px', left: '50%', transform: 'translateX(-50%)', background: 'white', width: '90%', borderRadius: '14px', padding: '18px', display: 'flex', justifyContent: 'space-around', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#3c5bff', fontSize: '20px', fontWeight: 'bold' }}>1,600+</div>
                                <div style={{ fontSize: '12px', color: '#444' }}>Businesses Ranked</div>
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#00a23a', fontSize: '20px', fontWeight: 'bold' }}>73 days</div>
                                <div style={{ fontSize: '12px', color: '#444' }}>Avg. Results</div>
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#8b4dff', fontSize: '20px', fontWeight: 'bold' }}>8933</div>
                                <div style={{ fontSize: '12px', color: '#444' }}>Issues Fixed</div>
                            </div>
                        </div>

                    </div>

                </div>

            </section>

            {/* PROBLEM SECTION */}
            <section className="problem-section">
                <div className="problem-section__container">
                    <div className="problem-section__badge">
                        <span role="img" aria-label="warning">
                            ⚠️
                        </span>
                        <span>The Hidden Problem</span>
                    </div>

                    <h1 className="problem-section__title">
                        Why <span className="problem-section__highlight">90%</span> of local businesses<br />
                        stay invisible online
                    </h1>

                    <div className="problem-section__grid">
                        <div className="problem-section__card">
                            <div className="problem-section__card-header">
                                <div className="problem-section__icon" aria-hidden="true">📉</div>
                                <div>
                                    <h3 className="problem-section__card-title">Inconsistent Google Profile Management</h3>
                                    <p className="problem-section__card-description">
                                        Manual updates never work. Posts, reviews, photos, and profile details stay irregular — causing your Google rankings to drop without warning.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="problem-section__card">
                            <div className="problem-section__card-header">
                                <div className="problem-section__icon" aria-hidden="true">📚</div>
                                <div>
                                    <h3 className="problem-section__card-title">Scattered & Unreliable Data</h3>
                                    <p className="problem-section__card-description">
                                        Your data sits across multiple tools, spreadsheets, and team members. No single dashboard shows what's improving or declining.

                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="problem-section__card">
                            <div className="problem-section__card-header">
                                <div className="problem-section__icon" aria-hidden="true">⚡</div>
                                <div>
                                    <h3 className="problem-section__card-title">No Real Visibility Into Rankings</h3>
                                    <p className="problem-section__card-description">
                                        Even after spending on marketing, you rarely know:
                                        “Are my Google Maps rankings actually going up or down?”
                                        Most businesses have zero clarity.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="problem-section__card">
                            <div className="problem-section__card-header">
                                <div className="problem-section__icon" aria-hidden="true">📍</div>
                                <div>
                                    <h3 className="problem-section__card-title">Unmonitored Activity Hurts Rankings</h3>
                                    <p className="problem-section__card-description">
                                        NAP issues, wrong categories, outdated photos, missing posts, and review gaps silently kill visibility — because no system monitors them daily.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="problem-section__cta">
                        <p className="problem-section__cta-text">✨ There's a better way.</p>
                        <button
                            className="problem-section__cta-button"
                            onClick={handleLoginNavigation}
                        >
                            See How Visibeen Solves This, Start Your Audit in Just ₹1
                            <span aria-hidden="true">→</span>
                        </button>
                    </div>
                </div>
            </section>

            {/* SOLUTION SECTION - New Design */}
            <div id="features" className="outer" style={{ maxWidth: 'auto', margin: '0 auto', padding: '80px 40px', textAlign: 'center', background: 'linear-gradient(135deg, #155DFC 0%, #4F39F6 50%, #9810FA 100%)' }}>

                {/* Badge */}
                <div style={{ display: 'inline-block', padding: '7px 20px', background: 'white', color: '#555', borderRadius: '30px', fontSize: '14px', boxShadow: '0 3px 10px rgba(0,0,0,0.15)' }}>
                    ✨ The Complete Solution
                </div>

                {/* Main Title */}
                <h1 className="title-1" style={{ fontSize: '52px', fontWeight: 800, margin: '25px 0 0', color: '#ffffff', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', textShadow: '0 4px 20px rgba(0,0,0,0.3)', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                    Six Reasons
                </h1>

                <h2 className="title-2" style={{ fontSize: '38px', fontWeight: 700, margin: '15px auto 0', color: '#FFD700', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', textShadow: '0 2px 15px rgba(255,215,0,0.4)', letterSpacing: '-0.01em', lineHeight: 1.2, textAlign: 'center' }}>
                    Businesses Switch to Visibeen
                </h2>

                {/* New Card Design */}
                <div className="solution-card" style={{ marginTop: '50px' }}>
                    <div className="solution-card-content">
                        {/* Header */}
                        <div className="solution-header">
                            <h2>With Visibeen you get:</h2>
                            <div className="solution-header-accent"></div>
                        </div>

                        {/* Features Grid */}
                        <div className="solution-features-grid">
                            {/* Feature 1 */}
                            <div className="solution-feature-card">
                                <div className="solution-feature-icon-wrapper solution-emerald">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3>Automated Google Business Profile Management</h3>
                                <p>Posts, updates, photos, responses and optimizations — handled automatically, consistently and on time.</p>
                            </div>

                            {/* Feature 2 */}
                            <div className="solution-feature-card">
                                <div className="solution-feature-icon-wrapper solution-blue">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                    </svg>
                                </div>
                                <h3>Competitor Tracking in Real-Time</h3>
                                <p>See exactly what nearby competitors are doing: their reviews, posts, ranking movement and activity patterns.</p>
                            </div>

                            {/* Feature 3 */}
                            <div className="solution-feature-card">
                                <div className="solution-feature-icon-wrapper solution-violet">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2z" />
                                    </svg>
                                </div>
                                <h3>Dynamic Visibeen Score™</h3>
                                <p>A single score that shows your true local visibility — updated daily with actionable tasks to improve rank.</p>
                            </div>

                            {/* Feature 4 */}
                            <div className="solution-feature-card">
                                <div className="solution-feature-icon-wrapper solution-orange">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <h3>Real-Time Google Maps Rank Heatmap</h3>
                                <p>Track your ranking for every keyword across your service radius. See exactly where you appear — street by street.</p>
                            </div>

                            {/* Feature 5 */}
                            <div className="solution-feature-card">
                                <div className="solution-feature-icon-wrapper solution-pink">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 12H9m6 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3>Unified Review & Profile Monitoring</h3>
                                <p>View all your reviews, ratings, profile updates, and GBP activity in one place. No scattered data — everything stays synced.</p>
                            </div>

                            {/* Feature 6 */}
                            <div className="solution-feature-card">
                                <div className="solution-feature-icon-wrapper solution-amber">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <h3>AI-Driven Recommendations</h3>
                                <p>Visibeen's AI tells you what to do next — based on your profile gaps, competitor strengths and ranking signals.</p>
                            </div>
                        </div>

                        {/* Callout Box */}
                        <div className="solution-callout">
                            <div className="solution-callout-icon">💡</div>
                            <p>Know your score. Improve your score. <span className="solution-callout-highlight">Outrank the rest.</span></p>
                        </div>

                        {/* Action Buttons */}
                        <div className="solution-button-group">
                            <a
                                href="#"
                                onClick={handleLoginNavigation}
                                className="solution-btn solution-btn-primary"
                            >
                                <span className="solution-btn-icon">🚀</span>
                                Start Your Audit in just ₹1 →
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* SUCCESS STORIES SECTION */}
            <section style={{ width: '100%', padding: '60px 20px', background: 'linear-gradient(135deg, #EFF6FF 0%, #FAF5FF 50%, #FDF2F8 100%)', boxSizing: 'border-box' }}>

                <div style={{ maxWidth: '1400px', margin: '0 auto', textAlign: 'center' }}>

                    {/* Badge */}
                    <div style={{ display: 'inline-block', padding: '7px 20px', background: '#DCFCE7', color: '#008236', borderRadius: '30px', fontSize: '14px', boxShadow: '0 3px 10px rgba(0,0,0,0.15)', whiteSpace: 'nowrap' }}>
                        ⭐ Success Stories
                    </div>

                    {/* Main Title */}
                    <h1 style={{ fontSize: 'clamp(24px, 5vw, 42px)', fontWeight: 700, margin: '25px auto 0', color: '#101828', maxWidth: 'auto', lineHeight: 1.3 }}>
                        Trusted by local brands who've <span style={{ color: '#00A63E' }}>moved up the map.</span>
                    </h1>

                    {/* Reviews Carousel Container */}
                    <div className="reviews-carousel-container" style={{ position: 'relative', marginTop: '50px', padding: '0 30px' }}>
                        {/* Left Arrow */}
                        <button
                            className="reviews-nav-btn reviews-nav-btn-left"
                            onClick={() => scrollReviews('left')}
                            style={{
                                position: 'absolute',
                                left: '0px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '44px',
                                height: '44px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)',
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                                transition: 'all 0.3s ease'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(-50%)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)'; }}
                        >
                            <span style={{ fontSize: '18px', color: '#374151', fontWeight: 600 }}>‹</span>
                        </button>

                        {/* Right Arrow */}
                        <button
                            className="reviews-nav-btn reviews-nav-btn-right"
                            onClick={() => scrollReviews('right')}
                            style={{
                                position: 'absolute',
                                right: '0px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '44px',
                                height: '44px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)',
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                                transition: 'all 0.3s ease'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(-50%)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)'; }}
                        >
                            <span style={{ fontSize: '18px', color: '#374151', fontWeight: 600 }}>›</span>
                        </button>

                        {/* Reviews Row */}
                        <div
                            ref={reviewsRef}
                            className="reviews-grid"
                            style={{
                                display: 'flex',
                                gap: '24px',
                                overflowX: 'auto',
                                paddingBottom: '10px',
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                WebkitOverflowScrolling: 'touch'
                            }}
                        >

                            {/* Review 1 - Ashu Bike Dealer */}
                            <div className="review-card" style={{ background: 'linear-gradient(135deg, #155DFC 0%, #432DD7 100%)', borderRadius: '20px', padding: '25px 30px', boxShadow: '0 10px 35px rgba(0,0,0,0.15)', textAlign: 'left', color: 'white', minHeight: '280px', minWidth: '300px', flex: '0 0 300px' }}>

                                {/* Stars */}
                                <p style={{ fontSize: '18px', margin: '0 0 15px' }}>
                                    ⭐⭐⭐⭐⭐
                                </p>

                                {/* Review Text */}
                                <div style={{ fontFamily: 'Arimo, Arial, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: 1.4 }}>
                                    Thank you team for handling my profile from day one to now it's the same and good response and I use to get daily business calls. They also keep posting content on profile. Anyone looking digital marketing company must check team e2e once.
                                </div>

                                {/* Profile Row */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
                                    <div style={{ background: 'rgba(255,255,255,0.26)', color: 'white', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold' }}>AB</div>

                                    <div style={{ fontSize: '13px', color: '#ffffff', lineHeight: 1.3 }}>
                                        <strong>Ashu Bike Dealer</strong><br />
                                        Automotive Business
                                    </div>
                                </div>

                                {/* Footer Note */}
                                <div style={{ marginTop: '15px', borderRadius: '10px', borderTop: '1px solid rgba(255,255,255,0.25)', padding: '12px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.18)', lineHeight: 1.3 }}>
                                    📊 Available on request: case study with full metrics and timeline
                                </div>

                            </div>

                            {/* Review 2 - Ghadiyal Kumar */}
                            <div className="review-card" style={{ background: 'linear-gradient(135deg, #00A63E 0%, #059669 100%)', borderRadius: '20px', padding: '25px 30px', boxShadow: '0 10px 35px rgba(0,0,0,0.15)', textAlign: 'left', color: 'white', minHeight: '280px', minWidth: '300px', flex: '0 0 300px' }}>

                                {/* Stars */}
                                <p style={{ fontSize: '18px', margin: '0 0 15px' }}>
                                    ⭐⭐⭐⭐⭐
                                </p>

                                {/* Review Text */}
                                <div style={{ fontFamily: 'Arimo, Arial, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: 1.4 }}>
                                    3rd year of renewal. Bhut Acha experience raha jo bola whi Kia Mera businesses Acha incense hua hai. They pick every call and reply on time. Ache se they handle my google business profile.
                                </div>

                                {/* Profile Row */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
                                    <div style={{ background: 'rgba(255,255,255,0.26)', color: 'white', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold' }}>GK</div>

                                    <div style={{ fontSize: '13px', color: '#ffffff', lineHeight: 1.3 }}>
                                        <strong>Ghadiyal Kumar</strong><br />
                                        Local Business Owner
                                    </div>
                                </div>

                                {/* Footer Note */}
                                <div style={{ marginTop: '15px', borderRadius: '10px', borderTop: '1px solid rgba(255,255,255,0.25)', padding: '12px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.18)', lineHeight: 1.3 }}>
                                    🎯 3+ Years Client • Consistent Growth
                                </div>

                            </div>

                            {/* Review 3 - Dharmender Kumar Gupta */}
                            <div className="review-card" style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)', borderRadius: '20px', padding: '25px 30px', boxShadow: '0 10px 35px rgba(0,0,0,0.15)', textAlign: 'left', color: 'white', minHeight: '280px', minWidth: '300px', flex: '0 0 300px' }}>

                                {/* Stars */}
                                <p style={{ fontSize: '18px', margin: '0 0 15px' }}>
                                    ⭐⭐⭐⭐⭐
                                </p>

                                {/* Review Text */}
                                <div style={{ fontFamily: 'Arimo, Arial, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: 1.4 }}>
                                    We are very impressed with the service of E2E Digitech... Team is very professional... Keep the good work... Highly recommended...
                                </div>

                                {/* Profile Row */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
                                    <div style={{ background: 'rgba(255,255,255,0.26)', color: 'white', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold' }}>DG</div>

                                    <div style={{ fontSize: '13px', color: '#ffffff', lineHeight: 1.3 }}>
                                        <strong>Dharmender Kumar Gupta</strong><br />
                                        Business Professional
                                    </div>
                                </div>

                                {/* Footer Note */}
                                <div style={{ marginTop: '15px', borderRadius: '10px', borderTop: '1px solid rgba(255,255,255,0.25)', padding: '12px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.18)', lineHeight: 1.3 }}>
                                    ⭐ Highly Recommended • Professional Service
                                </div>

                            </div>

                            {/* Review 4 - Mandeep Kaur */}
                            <div className="review-card" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)', borderRadius: '20px', padding: '25px 30px', boxShadow: '0 10px 35px rgba(0,0,0,0.15)', textAlign: 'left', color: 'white', minHeight: '280px', minWidth: '300px', flex: '0 0 300px' }}>

                                {/* Stars */}
                                <p style={{ fontSize: '18px', margin: '0 0 15px' }}>
                                    ⭐⭐⭐⭐⭐
                                </p>

                                {/* Review Text */}
                                <div style={{ fontFamily: 'Arimo, Arial, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: 1.4 }}>
                                    We've been associated with E2E Digitech Pvt. Ltd. for over a year now, and it's been a smooth and rewarding journey. Their SEO services really delivered – we saw a big improvement in our visibility and leads. What I appreciate the most is their responsive and supportive team – they always pick up our calls and help us out whenever we need. Great team, great results!
                                </div>

                                {/* Profile Row */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
                                    <div style={{ background: 'rgba(255,255,255,0.26)', color: 'white', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold' }}>MK</div>

                                    <div style={{ fontSize: '13px', color: '#ffffff', lineHeight: 1.3 }}>
                                        <strong>Mandeep Kaur</strong><br />
                                        Business Owner
                                    </div>
                                </div>

                                {/* Footer Note */}
                                <div style={{ marginTop: '15px', borderRadius: '10px', borderTop: '1px solid rgba(255,255,255,0.25)', padding: '12px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.18)', lineHeight: 1.3 }}>
                                    📈 1+ Year Client • Improved Visibility & Leads
                                </div>

                            </div>

                            {/* Review 5 - Mohit Sharma */}
                            <div className="review-card" style={{ background: 'linear-gradient(135deg, #EC4899 0%, #F43F5E 100%)', borderRadius: '20px', padding: '25px 30px', boxShadow: '0 10px 35px rgba(0,0,0,0.15)', textAlign: 'left', color: 'white', minHeight: '280px', minWidth: '300px', flex: '0 0 300px' }}>

                                {/* Stars */}
                                <p style={{ fontSize: '18px', margin: '0 0 15px' }}>
                                    ⭐⭐⭐⭐⭐
                                </p>

                                {/* Review Text */}
                                <div style={{ fontFamily: 'Arimo, Arial, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: 1.4 }}>
                                    Great experience with E2E Digitech Pvt. Ltd.! Their SEO strategies really work—we saw a noticeable boost in traffic and inquiries within a few weeks. What I appreciate most is their coordination and support. Highly recommended!
                                </div>

                                {/* Profile Row */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
                                    <div style={{ background: 'rgba(255,255,255,0.26)', color: 'white', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold' }}>MS</div>

                                    <div style={{ fontSize: '13px', color: '#ffffff', lineHeight: 1.3 }}>
                                        <strong>Mohit Sharma</strong><br />
                                        Business Owner
                                    </div>
                                </div>

                                {/* Footer Note */}
                                <div style={{ marginTop: '15px', borderRadius: '10px', borderTop: '1px solid rgba(255,255,255,0.25)', padding: '12px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.18)', lineHeight: 1.3 }}>
                                    🚀 Boost in Traffic & Inquiries
                                </div>

                            </div>

                        </div>
                    </div>

                </div>
            </section>

            {/* PRICING SECTION */}
            <section id="pricing" style={{ margin: 0, padding: '40px 20px', fontFamily: 'Inter, sans-serif', background: 'linear-gradient(135deg,#eef2ff 0%,#e8f5ff 40%,#f3e8ff 100%)', display: 'flex', justifyContent: 'center' }}>
                <div style={{ maxWidth: '1100px', width: '100%', textAlign: 'center' }}>

                    {/* Badge */}
                    <div style={{ display: 'inline-block', background: 'white', padding: '8px 20px', borderRadius: '30px', fontSize: '14px', color: '#333', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginBottom: '25px' }}>
                        💎 Transparent Pricing
                    </div>

                    <h1 style={{ fontSize: '40px', fontWeight: 600, marginBottom: '10px' }}>
                        Start your journey to
                        <span style={{ color: '#5b3dfd' }}> local dominance</span>
                    </h1>

                    <p style={{ color: '#777', marginBottom: '50px' }}>
                        Choose the plan that fits your scale. No hidden fees. Cancel anytime.Money Back Guarantee
                    </p>

                    <div style={{ display: 'inline-flex', borderRadius: '999px', background: 'rgba(59,130,246,0.08)', padding: '4px', marginBottom: '30px' }}>
                        {['monthly', 'yearly'].map((cycle) => (
                            <button
                                key={cycle}
                                onClick={() => setBillingCycle(cycle)}
                                style={{
                                    border: 'none',
                                    padding: '8px 18px',
                                    borderRadius: '999px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    background: billingCycle === cycle ? '#3b82f6' : 'transparent',
                                    color: billingCycle === cycle ? 'white' : '#1f2937'
                                }}
                            >
                                {cycle === 'monthly' ? 'Monthly' : 'Yearly'}
                            </button>
                        ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', marginBottom: '40px' }}>
                        {pricingPlansData[billingCycle].map((plan) => (
                            <div key={plan.name} style={{ background: 'white', borderRadius: '18px', padding: '28px 24px', position: 'relative', boxShadow: '0 10px 25px rgba(0,0,0,0.08)', border: plan.featured ? '2px solid #5b3dfd' : '1px solid #e0e7ff' }}>
                                {plan.featured && (
                                    <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: '#00c896', color: 'white', padding: '6px 16px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, boxShadow: '0 6px 14px rgba(0,0,0,0.15)' }}>
                                        Most Popular
                                    </div>
                                )}
                                <h3 style={{ fontSize: '26px', margin: '20px 0 6px', fontWeight: 600 }}>{plan.price}</h3>
                                <p style={{ fontSize: '15px', color: '#4b5563', margin: '0 0 18px' }}>{plan.description}</p>
                                <div style={{ textAlign: 'left', fontSize: '15px', lineHeight: 1.7, color: '#111827', minHeight: '160px' }}>
                                    {plan.features.map((feature, index) => (
                                        <p key={index} style={{ margin: '6px 0', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                            <span>✅</span>
                                            <span>{feature}</span>
                                        </p>
                                    ))}
                                </div>

                                <button onClick={() => navigate('/login')} style={{ marginTop: '20px', width: '100%', padding: '12px 0', borderRadius: '10px', border: 'none', background: plan.featured ? 'linear-gradient(90deg,#5b3dfd,#a855f7)' : '#4338ca', color: 'white', fontWeight: 600, fontSize: '15px', cursor: 'pointer' }}>
                                    Get Started
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Help Section */}
                    <div style={{ background: 'white', padding: '25px', borderRadius: '14px', maxWidth: '600px', margin: '0 auto 20px', boxShadow: '0 6px 18px rgba(0,0,0,0.07)' }}>
                        <p style={{ fontSize: '15px', marginBottom: '12px' }}>
                            🍉 Not sure which plan?
                            <a href="#" onClick={(e) => { e.preventDefault(); setShowContactModal(true); }} style={{ color: '#3b82f6', textDecoration: 'none', cursor: 'pointer' }}> Let's help you decide!</a>
                        </p>

                        <button onClick={() => setShowContactModal(true)} style={{ background: 'white', padding: '12px 22px', border: '1px solid #3b82f6', color: '#3b82f6', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
                            📞 Schedule a Free Consultation
                        </button>
                    </div>

                    {/* Guarantees */}
                    <div style={{ color: '#444', marginBottom: '20px', fontSize: '14px' }}>
                        ✅ No long-term lock-in • ⬆️ Cancel anytime • 💰 Money-back guarantee
                    </div>

                    {/* Access Banner */}
                    <div style={{ background: '#09c46a', color: 'white', padding: '18px 20px', borderRadius: '12px', maxWidth: '600px', margin: '0 auto', fontSize: '16px', boxShadow: '0 6px 18px rgba(0,0,0,0.1)' }}>
                        🧩 Get access now — we'll walk you through your first Visibeen Score live.
                    </div>

                </div>
            </section>

            {/* FAQ SECTION */}
            <section id="faq" style={{ fontFamily: 'Arial, sans-serif', padding: '40px 20px', maxWidth: '1050px', margin: 'auto', textAlign: 'center' }}>

                {/* Top Badge */}
                <div style={{ display: 'inline-block', background: '#f5eaff', color: '#8a4dfd', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', marginBottom: '18px' }}>
                    ❓ Got Questions?
                </div>

                {/* Heading */}
                <h2 style={{ fontSize: '32px', margin: 0, color: '#0b1a33' }}>Frequently Asked Questions</h2>

                <p style={{ color: '#5a5a5a', marginTop: '8px', fontSize: '15px' }}>
                    Everything you need to know about Visibeen
                </p>

                {/* FAQ Container */}
                <div style={{ marginTop: '30px', textAlign: 'left' }}>

                    {[
                        {
                            q: "How long until I see results?",
                            a: "While every business is unique, many users begin seeing ranking improvements within 60–90 days of using Visibeen actively. Some see changes even sooner!"
                        },
                        {
                            q: "Do I need an SEO background?",
                            a: "No SEO experience is required. The system is designed to be simple, intuitive, and fully automated — giving clear guidance on what happens behind the scenes."
                        },
                        {
                            q: "Can I manage multiple locations / clients?",
                            a: "Yes! You can easily switch between locations, add new clients, and manage everything from a single dashboard."
                        },
                        {
                            q: "Is it just auditing or full execution?",
                            a: "You get both: full auditing + execution. We monitor issues, fix them, and keep your profiles optimized automatically."
                        },
                        {
                            q: "What if my profile is suspended by Google?",
                            a: "Our team can help guide you through the reinstatement process and ensure all requirements are met for quick recovery."
                        }
                    ].map((faq, index) => (
                        <div key={index} style={{ marginBottom: '12px' }}>
                            <div onClick={() => toggleFAQ(index)} style={{ background: '#f2f7ff', padding: '16px 20px', borderRadius: '10px', border: '1px solid #e8eef7', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                                <span style={{ fontSize: '15px' }}>{faq.q}</span>
                                <span style={{ fontSize: '18px', color: '#777' }}>{activeFAQ === index ? '−' : '⌄'}</span>
                            </div>

                            <div style={{ maxHeight: activeFAQ === index ? '200px' : '0', overflow: 'hidden', transition: 'max-height 0.3s ease', background: 'white', borderRadius: '0 0 10px 10px', border: activeFAQ === index ? '1px solid #e8eef7' : 'none', borderTop: 'none', padding: activeFAQ === index ? '14px 20px' : '0 20px' }}>
                                <p style={{ fontSize: '14px', color: '#444', margin: 0 }}>
                                    {faq.a}
                                </p>
                            </div>
                        </div>
                    ))}

                </div>
            </section>

            {/* FINAL CTA SECTION */}
            <section style={{ margin: 0, padding: 0, fontFamily: 'Inter, sans-serif', background: 'radial-gradient(circle at 50% 30%, #153778, #051d46 60%, #02112d 100%)', color: 'white', textAlign: 'center' }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '80px 20px' }}>

                    {/* Top Stats Row */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '18px', flexWrap: 'wrap', marginBottom: '40px' }}>

                        <div style={{ background: 'rgba(255,255,255,0.1)', padding: '10px 18px', borderRadius: '30px', fontSize: '14px', backdropFilter: 'blur(6px)' }}>⚡ 10-min setup</div>

                        <div style={{ background: 'rgba(255,255,255,0.1)', padding: '10px 18px', borderRadius: '30px', fontSize: '14px', backdropFilter: 'blur(6px)' }}>📈 93% success rate</div>

                        <div style={{ background: 'rgba(255,255,255,0.1)', padding: '10px 18px', borderRadius: '30px', fontSize: '14px', backdropFilter: 'blur(6px)' }}>👥 1,623+ happy clients</div>
                    </div>

                    {/* Heading */}
                    <h1 style={{ fontSize: '48px', lineHeight: 1.25, marginBottom: '20px', fontWeight: 600 }}>
                        Ready to rank
                        <span style={{ background: 'linear-gradient(90deg,#ffba08,#ff6f59)', WebkitBackgroundClip: 'text', color: 'transparent' }}> higher</span>,
                        <span style={{ background: 'linear-gradient(90deg,#2ec4b6,#16db65)', WebkitBackgroundClip: 'text', color: 'transparent' }}> faster</span>
                        {' '}and
                        <span style={{ background: 'linear-gradient(90deg,#a78bfa,#60a5fa)', WebkitBackgroundClip: 'text', color: 'transparent' }}> smarter?</span>
                    </h1>

                    <p style={{ color: '#cfd9ff', fontSize: '17px', maxWidth: '650px', margin: '0 auto 40px' }}>
                        Don't let your competitors show up first. Take full control of your local
                        search presence with Visibeen.
                    </p>

                    {/* CTA Button */}
                    <a href="/login" style={{ display: 'inline-block', background: 'linear-gradient(90deg,#ff4d4d,#e60073)', padding: '15px 32px', borderRadius: '12px', color: 'white', fontSize: '18px', textDecoration: 'none', marginBottom: '40px', boxShadow: '0 6px 20px rgba(255,0,80,0.4)' }}>
                        🍉 Reveal My Visibeen Score Now →
                    </a>



                    {/* Divider */}
                    <div style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.15)', margin: '50px auto' }}></div>

                    {/* Industries Section */}
                    <p style={{ color: '#cfd9ff', marginBottom: '25px', fontSize: '15px' }}>
                        Join these local businesses who transformed their rankings:
                    </p>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap', fontSize: '15px', color: '#e4eaff' }}>
                        <div>🏥 Healthcare</div>
                        <div>🍽️ Restaurants</div>
                        <div>🏪 Retail</div>
                        <div>⚖️ Legal Services</div>
                        <div>🏡 Real Estate</div>
                        <div>💆 Beauty & Wellness</div>
                    </div>

                </div>
            </section>

            {/* FOOTER */}
            <footer style={{ width: '100%', background: 'linear-gradient(180deg, #0d1626 0%, #0a1019 100%)', color: '#dbe2f1' }}>

                {/* Main Footer Content */}
                <div className="footer-main-content" style={{ maxWidth: '1200px', margin: '0 auto', padding: '50px 20px 30px' }}>

                    {/* Top Section - Logo & Newsletter */}
                    <div className="footer-container" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '32px', marginBottom: '32px' }}>

                        {/* Left - Brand Section */}
                        <div className="footer-left" style={{ flex: '1 1 320px', maxWidth: '380px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <img src={VisibeenLogo} alt="Visibeen Logo" style={{ height: '36px', maxWidth: '140px' }} />
                            </div>
                            <p style={{ color: '#9aa5b8', lineHeight: 1.7, fontSize: '15px', margin: 0 }}>
                                Your all-in-one local SEO command centre. Dominate local search and get found first in your neighbourhood.
                            </p>

                            {/* Social Icons */}
                            <div className="footer-social" style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                                <a href="https://www.instagram.com/visibeen/" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,0.06)', transition: 'all 0.3s ease', border: '1px solid rgba(255,255,255,0.08)' }} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(131, 58, 180, 0.3)'; e.currentTarget.style.borderColor = 'rgba(131, 58, 180, 0.5)'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}>
                                    <InstagramIcon width={20} height={20} color="#9aa5b8" />
                                </a>
                                <a href="https://www.facebook.com/profile.php?id=61566867428338" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,0.06)', transition: 'all 0.3s ease', border: '1px solid rgba(255,255,255,0.08)' }} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(24, 119, 242, 0.3)'; e.currentTarget.style.borderColor = 'rgba(24, 119, 242, 0.5)'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}>
                                    <FacebookIcon width={20} height={20} color="#9aa5b8" />
                                </a>
                                <a href="https://www.youtube.com/@Visibeen" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,0.06)', transition: 'all 0.3s ease', border: '1px solid rgba(255,255,255,0.08)' }} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 0, 0, 0.3)'; e.currentTarget.style.borderColor = 'rgba(255, 0, 0, 0.5)'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}>
                                    <YouTubeIcon width={20} height={20} color="#9aa5b8" />
                                </a>
                                <a href="https://www.linkedin.com/company/visibeen/" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,0.06)', transition: 'all 0.3s ease', border: '1px solid rgba(255,255,255,0.08)' }} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(10, 102, 194, 0.3)'; e.currentTarget.style.borderColor = 'rgba(10, 102, 194, 0.5)'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}>
                                    <LinkedInIcon width={20} height={20} color="#9aa5b8" />
                                </a>
                            </div>
                        </div>

                        {/* Right - Newsletter */}
                        <div className="footer-newsletter" style={{ flex: '1 1 450px', maxWidth: '550px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'white', margin: '0 0 8px 0', textAlign: 'left' }}>Stay Updated</h3>
                            <p style={{ color: '#9aa5b8', fontSize: '14px', lineHeight: 1.5, margin: '0 0 14px 0', textAlign: 'left' }}>
                                Get the latest local SEO tips and product updates delivered to your inbox.
                            </p>
                            <div className="footer-newsletter-form" style={{ display: 'flex', gap: '12px', width: '100%' }}>
                                <input
                                    type="email"
                                    placeholder="Enter your email address"
                                    style={{
                                        flex: 2,
                                        padding: '16px 20px',
                                        borderRadius: '10px',
                                        border: '1px solid rgba(255,255,255,0.15)',
                                        background: 'rgba(255,255,255,0.08)',
                                        color: 'white',
                                        fontSize: '15px',
                                        outline: 'none',
                                        minWidth: '240px',
                                        width: '100%',
                                        boxSizing: 'border-box'
                                    }}
                                />
                                <button
                                    style={{
                                        padding: '16px 24px',
                                        borderRadius: '10px',
                                        border: 'none',
                                        background: 'linear-gradient(135deg, #3c5bff, #6a29ff)',
                                        color: 'white',
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        whiteSpace: 'nowrap'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(106, 41, 255, 0.4)'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                                >
                                    Subscribe
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.08)', margin: '0 0 28px 0' }}></div>

                    {/* Links Section */}
                    <div className="footer-columns" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '28px', marginBottom: '0' }}>

                        {/* Product Column */}
                        <div className="footer-column" style={{ flex: '1 1 150px', minWidth: '150px' }}>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                <li><a href="#pricing" style={{ textDecoration: 'none', color: '#9aa5b8', fontSize: '15px', transition: 'color 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'} onMouseOut={(e) => e.currentTarget.style.color = '#9aa5b8'}>Pricing</a></li>
                                <li><a href="#faq" style={{ textDecoration: 'none', color: '#9aa5b8', fontSize: '15px', transition: 'color 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'} onMouseOut={(e) => e.currentTarget.style.color = '#9aa5b8'}>FAQ</a></li>
                            </ul>
                        </div>

                        {/* Company Column */}
                        <div className="footer-column" style={{ flex: '1 1 150px', minWidth: '150px' }}>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                <li><a href="#" onClick={(e) => { e.preventDefault(); setShowAboutModal(true); }} style={{ textDecoration: 'none', color: '#9aa5b8', fontSize: '15px', transition: 'color 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'} onMouseOut={(e) => e.currentTarget.style.color = '#9aa5b8'}>About Us</a></li>
                                <li><a href="#" onClick={(e) => { e.preventDefault(); setShowContactModal(true); }} style={{ textDecoration: 'none', color: '#9aa5b8', fontSize: '15px', transition: 'color 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'} onMouseOut={(e) => e.currentTarget.style.color = '#9aa5b8'}>Contact</a></li>
                            </ul>
                        </div>



                        {/* Legal Column */}
                        <div className="footer-column" style={{ flex: '1 1 150px', minWidth: '150px' }}>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                <li><a href="#" onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }} style={{ textDecoration: 'none', color: '#9aa5b8', fontSize: '15px', transition: 'color 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'} onMouseOut={(e) => e.currentTarget.style.color = '#9aa5b8'}>Privacy Policy</a></li>
                                <li><a href="#" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }} style={{ textDecoration: 'none', color: '#9aa5b8', fontSize: '15px', transition: 'color 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'} onMouseOut={(e) => e.currentTarget.style.color = '#9aa5b8'}>Terms of Service</a></li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)' }}>
                    <div className="footer-bottom" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 24px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                        <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                            © 2025 Visibeen. All rights reserved.
                        </p>
                        <div className="footer-bottom-links" style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                            <a href="#" style={{ color: '#6b7280', fontSize: '14px', textDecoration: 'none', transition: 'color 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.color = '#9aa5b8'} onMouseOut={(e) => e.currentTarget.style.color = '#6b7280'}>Sitemap</a>
                            <a href="#" style={{ color: '#6b7280', fontSize: '14px', textDecoration: 'none', transition: 'color 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.color = '#9aa5b8'} onMouseOut={(e) => e.currentTarget.style.color = '#6b7280'}>Accessibility</a>
                            <a href="#" style={{ color: '#6b7280', fontSize: '14px', textDecoration: 'none', transition: 'color 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.color = '#9aa5b8'} onMouseOut={(e) => e.currentTarget.style.color = '#6b7280'}>Status</a>
                        </div>
                    </div>
                </div>

            </footer>

            {/* About Us Modal */}
            {showAboutModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    backdropFilter: 'blur(5px)'
                }} onClick={() => setShowAboutModal(false)}>
                    <div style={{
                        background: 'white',
                        borderRadius: '20px',
                        padding: '40px',
                        maxWidth: '800px',
                        width: '90%',
                        maxHeight: '85vh',
                        overflowY: 'auto',
                        position: 'relative',
                        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
                        cursor: 'default'
                    }} onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setShowAboutModal(false)} style={{ position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#666' }}>×</button>

                        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                            <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#111', marginBottom: '10px' }}>About Us</h2>
                            <p style={{ fontSize: '18px', color: '#6a29ff', fontWeight: 600 }}>Be Seen. Be Chosen. Be Local.</p>
                        </div>

                        <div style={{ lineHeight: 1.8, color: '#444', fontSize: '16px' }}>
                            <p>At Visibeen, we believe visibility is the new currency. Every local business deserves to be discovered where it matters most — on Google. That's why we built Visibeen: a powerful yet simple Local SEO platform that helps you show up, stand out, and succeed.</p>

                            <h3 style={{ marginTop: '30px', color: '#111' }}>The Pillars of Visibeen</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginTop: '20px' }}>
                                <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', borderLeft: '4px solid #4CAF50' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: '#4CAF50' }}>Diagnose</h4>
                                    <p style={{ margin: 0, fontSize: '14px' }}>We start by analyzing your Google Business Profile and local presence. From missing details to performance gaps, Visibeen gives you a clear picture of where you stand.</p>
                                </div>
                                <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', borderLeft: '4px solid #2196F3' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: '#2196F3' }}>Benchmarking</h4>
                                    <p style={{ margin: 0, fontSize: '14px' }}>With the Visibeen™ Score️, you can instantly compare your visibility against competitors and industry standards. It's your compass for local SEO success.</p>
                                </div>
                                <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', borderLeft: '4px solid #FF9800' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: '#FF9800' }}>Tasks</h4>
                                    <p style={{ margin: 0, fontSize: '14px' }}>Insights turn into actions. Visibeen generates prioritized, AI-powered tasks that help you improve step by step — from posting updates to managing reviews.</p>
                                </div>
                                <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', borderLeft: '4px solid #9C27B0' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: '#9C27B0' }}>Results</h4>
                                    <p style={{ margin: 0, fontSize: '14px' }}>Track measurable growth in visibility, traffic, and leads. Every completed task feeds back into your score, ensuring progress is visible and rewarding.</p>
                                </div>
                            </div>

                            <h3 style={{ marginTop: '30px', color: '#111' }}>Who We Are</h3>
                            <p>We're not just a software company — we're a team of marketers, builders, and problem-solvers passionate about helping businesses thrive in the digital-first world. From family-run shops to fast-growing brands, our mission is to make local search easy, transparent, and result-driven.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Privacy Policy Modal */}
            {showPrivacyModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    backdropFilter: 'blur(5px)'
                }} onClick={() => setShowPrivacyModal(false)}>
                    <div style={{
                        background: 'white',
                        borderRadius: '20px',
                        padding: '40px',
                        maxWidth: '800px',
                        width: '90%',
                        maxHeight: '85vh',
                        overflowY: 'auto',
                        position: 'relative',
                        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
                        cursor: 'default'
                    }} onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setShowPrivacyModal(false)} style={{ position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#666' }}>×</button>

                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                            <h2 style={{ fontSize: '32px', fontWeight: 700, color: '#111' }}>Privacy Policy</h2>
                            <p style={{ color: '#666' }}>Visibeen – Google Business Profile & AI Usage Terms</p>
                        </div>

                        <div style={{ lineHeight: 1.7, color: '#444', fontSize: '15px' }}>
                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>1. Your Account & Access</h3>
                                <ul style={{ paddingLeft: '20px' }}>
                                    <li>By connecting your Google Business Profile (GBP) to Visibeen, you grant us limited access to help manage, audit, and optimize your profile.</li>
                                    <li>Keep your login credentials secure. Sharing your account may result in suspension.</li>
                                </ul>
                            </section>

                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>2. How We Help</h3>
                                <ul style={{ paddingLeft: '20px' }}>
                                    <li>Visibeen provides tools to audit your GBP, calculate your Visibeen Score, suggest tasks, and compare with competitors.</li>
                                    <li>AI recommendations are advisory only. You must review and approve all suggestions before taking action.</li>
                                </ul>
                            </section>

                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>3. AI & Content Use</h3>
                                <ul style={{ paddingLeft: '20px' }}>
                                    <li>AI may generate posts, replies, or optimization ideas, but errors or inaccuracies may occur.</li>
                                    <li>Since AI Prompts are highly tested but since its machine model & Visibeen is not responsible for any outcomes resulting from AI recommendations, including posted content.</li>
                                </ul>
                            </section>

                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>4. Your Responsibilities</h3>
                                <ul style={{ paddingLeft: '20px' }}>
                                    <li>Ensure all AI suggestions and actions comply with Google policies and applicable laws.</li>
                                    <li>Maintain accurate business information in your profile.</li>
                                    <li>Verify all content before posting.</li>
                                </ul>
                            </section>

                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>5. Limitation of Liability</h3>
                                <p>Visibeen is not liable for any:</p>
                                <ul style={{ paddingLeft: '20px' }}>
                                    <li>Loss of profile rankings, reviews, or visibility</li>
                                    <li>Suspension or penalties by Google</li>
                                    <li>Errors, omissions, or damages from AI-generated content</li>
                                    <li>Any financial or reputational loss</li>
                                </ul>
                            </section>

                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>6. Payment Authorization & Mandate Activation</h3>
                                <ul style={{ paddingLeft: '20px' }}>
                                    <li>By subscribing to Visibeen, you authorize us to initiate recurring payments through NPCI-approved e-mandate or UPI autopay systems.</li>
                                    <li>Mandate activation is required to access services, including GBP management and AI features.</li>
                                </ul>
                            </section>

                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>7. Refunds</h3>
                                <ul style={{ paddingLeft: '20px' }}>
                                    <li>All payments are non-refundable, except if Visibeen fails to provide access to services.</li>
                                    <li>Canceling stops future billing but does not refund the current cycle.</li>
                                </ul>
                            </section>
                        </div>
                    </div>
                </div>
            )}

            {/* Terms of Service Modal */}
            {showTermsModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    backdropFilter: 'blur(5px)'
                }} onClick={() => setShowTermsModal(false)}>
                    <div style={{
                        background: 'white',
                        borderRadius: '20px',
                        padding: '40px',
                        maxWidth: '800px',
                        width: '90%',
                        maxHeight: '85vh',
                        overflowY: 'auto',
                        position: 'relative',
                        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
                        cursor: 'default'
                    }} onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setShowTermsModal(false)} style={{ position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#666' }}>×</button>

                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                            <h2 style={{ fontSize: '32px', fontWeight: 700, color: '#111' }}>Terms of Service</h2>
                            <p style={{ color: '#666' }}>Visibeen – Google Business Profile & AI Usage Terms</p>
                        </div>

                        <div style={{ lineHeight: 1.7, color: '#444', fontSize: '15px' }}>
                            <p style={{ fontStyle: 'italic', marginBottom: '20px' }}>Policy for Website at time of paid version. This document outlines Visibeen's GBP and AI Usage Terms.</p>

                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', fontVariant: 'small-caps', marginBottom: '10px' }}>1. Your Account & Access</h3>
                                <p>By linking your Google Business Profile (GBP) to Visibeen, you grant us limited access to help manage, audit, and improve your profile. Keep your login credentials secure. Sharing your account may result in suspension.</p>
                            </section>

                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', fontVariant: 'small-caps', marginBottom: '10px' }}>2. How We Help</h3>
                                <p>Visibeen provides tools to audit your GBP, calculate your Visibeen Score, suggest tasks, and compare with competitors. All recommendations are advisory only. You must review and approve all suggestions before taking action.</p>
                            </section>

                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', fontVariant: 'small-caps', marginBottom: '10px' }}>3. AI & Content Use</h3>
                                <p>We use generative AI to suggest posts, replies, or optimization ideas. AI prompts are highly tested but since its machine model & Visibeen is not responsible for any outcomes resulting from AI-generated content.</p>
                            </section>

                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', fontVariant: 'small-caps', marginBottom: '10px' }}>4. Your Responsibilities</h3>
                                <p>Ensure all AI suggestions and actions comply with Google policies and applicable laws. Maintain accurate business information in your profile. Verify all content before posting.</p>
                            </section>

                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', fontVariant: 'small-caps', marginBottom: '10px' }}>5. Limitation of Liability</h3>
                                <p>Visibeen is not liable for loss of profile rankings, reviews, or visibility; suspension or penalties by Google; errors in AI-generated or user-generated content; or any financial or reputational loss.</p>
                            </section>

                            <section style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#111', fontSize: '18px', fontVariant: 'small-caps', marginBottom: '10px' }}>6. Payment Authorization</h3>
                                <p>If you enable auto-deduct, you authorize us to initiate recurring payments through NPCI-approved e-mandate or UPI billing systems. Mandate activation confirms your consent for automatic deduction of subscription fees.</p>
                            </section>
                        </div>
                    </div>
                </div>
            )}

            {/* Contact Modal */}
            {showContactModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    backdropFilter: 'blur(5px)'
                }} onClick={() => setShowContactModal(false)}>
                    <div style={{
                        background: 'white',
                        borderRadius: '20px',
                        padding: '40px',
                        maxWidth: '1000px',
                        width: '95%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        position: 'relative',
                        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
                        cursor: 'default'
                    }} onClick={(e) => e.stopPropagation()}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowContactModal(false)}
                            style={{
                                position: 'absolute',
                                top: '20px',
                                right: '20px',
                                background: 'none',
                                border: 'none',
                                fontSize: '28px',
                                cursor: 'pointer',
                                color: '#666',
                                zIndex: 10
                            }}
                        >
                            ×
                        </button>

                        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                            <p style={{ color: '#3b82f6', fontWeight: 600, letterSpacing: '2px', fontSize: '12px', textTransform: 'uppercase', marginBottom: '8px' }}>Get In Touch!</p>
                            <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#111', margin: 0 }}>Have Questions?</h2>
                            <p style={{ color: '#666', fontSize: '16px', marginTop: '10px' }}>Reach out to our team anytime. We're here to help.</p>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px' }}>
                            {/* Left Side: Contact Info */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '16px', display: 'flex', gap: '20px', alignItems: 'flex-start', border: '1px solid #e2e8f0' }}>
                                    <div style={{ background: '#dbeafe', padding: '12px', borderRadius: '12px', color: '#3b82f6', fontSize: '24px' }}>📍</div>
                                    <div>
                                        <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#111' }}>Visit Us</h4>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#444', lineHeight: 1.6 }}>
                                            Office No A-611, 6th floor, Bestech Tower<br />
                                            Mohali, SAS Nagar, Punjab
                                        </p>
                                    </div>
                                </div>

                                <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '16px', display: 'flex', gap: '20px', alignItems: 'flex-start', border: '1px solid #e2e8f0' }}>
                                    <div style={{ background: '#dbeafe', padding: '12px', borderRadius: '12px', color: '#3b82f6', fontSize: '24px' }}>📞</div>
                                    <div>
                                        <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#111' }}>Call Us</h4>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <a href="tel:+919815009722" style={{ textDecoration: 'none', color: '#3b82f6', fontWeight: 500, fontSize: '14px' }}>+91-9815009722</a>
                                            <a href="tel:+919115959722" style={{ textDecoration: 'none', color: '#3b82f6', fontWeight: 500, fontSize: '14px' }}>+91-9115959722</a>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '16px', display: 'flex', gap: '20px', alignItems: 'flex-start', border: '1px solid #e2e8f0' }}>
                                    <div style={{ background: '#dbeafe', padding: '12px', borderRadius: '12px', color: '#3b82f6', fontSize: '24px' }}>⏰</div>
                                    <div>
                                        <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#111' }}>Working Hours</h4>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#444', lineHeight: 1.6 }}>
                                            Mon-Fri: 9 AM – 6 PM<br />
                                            Sunday: Closed
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: Form */}
                            <div style={{ background: '#ffffff', borderRadius: '16px' }}>
                                <form style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Full Name *</label>
                                            <input type="text" placeholder="John Doe" style={{ width: '100%', padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Email Address *</label>
                                            <input type="email" placeholder="john@example.com" style={{ width: '100%', padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                                        </div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Phone Number *</label>
                                            <input type="tel" placeholder="+91 00000 00000" style={{ width: '100%', padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Business Name</label>
                                            <input type="text" placeholder="Your Business LLC" style={{ width: '100%', padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                                        </div>
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>How can we help you?</label>
                                        <textarea placeholder="Tell us about your business goals..." rows={4} style={{ width: '100%', padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}></textarea>
                                    </div>

                                    <button
                                        type="submit"
                                        style={{
                                            background: 'linear-gradient(135deg, #3c5bff, #6a29ff)',
                                            color: 'white',
                                            padding: '14px 24px',
                                            border: 'none',
                                            borderRadius: '10px',
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 4px 12px rgba(60, 91, 255, 0.3)'
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            alert('Thank you! We will contact you soon.');
                                            setShowContactModal(false);
                                        }}
                                    >
                                        Send Message
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Demo Modal */}
            {showDemoModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '32px',
                        maxWidth: '450px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        position: 'relative',
                        boxShadow: '0 25px 50px rgba(0,0,0,0.2)'
                    }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowDemoModal(false)}
                            style={{
                                position: 'absolute',
                                top: '16px',
                                right: '16px',
                                background: 'none',
                                border: 'none',
                                fontSize: '24px',
                                cursor: 'pointer',
                                color: '#666',
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            ×
                        </button>

                        {/* Modal Header */}
                        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                background: 'linear-gradient(135deg, #3c5bff, #6a29ff)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px',
                                fontSize: '24px'
                            }}>
                                🚀
                            </div>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1f2937' }}>
                                Book Your Demo
                            </h2>
                            <p style={{ margin: '8px 0 0', fontSize: '16px', color: '#6b7280' }}>
                                See Visibeen in action with a personalized demo
                            </p>
                        </div>

                        {/* Demo Form */}
                        <form onSubmit={handleDemoSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                                    Full Name *
                                </label>
                                <input
                                    type="text"
                                    name="fullName"
                                    value={demoFormData.fullName}
                                    onChange={handleDemoInputChange}
                                    placeholder="John Doe"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                                    Work Email *
                                </label>
                                <input
                                    type="email"
                                    name="workEmail"
                                    value={demoFormData.workEmail}
                                    onChange={handleDemoInputChange}
                                    placeholder="john@company.com"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                                    Phone Number *
                                </label>
                                <input
                                    type="tel"
                                    name="phoneNumber"
                                    value={demoFormData.phoneNumber}
                                    onChange={handleDemoInputChange}
                                    placeholder="+1 (555) 123-4567"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                                    Company Name *
                                </label>
                                <input
                                    type="text"
                                    name="companyName"
                                    value={demoFormData.companyName}
                                    onChange={handleDemoInputChange}
                                    placeholder="Your Company Inc."
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                                    Preferred Demo Date
                                </label>
                                <input
                                    type="date"
                                    name="preferredDate"
                                    value={demoFormData.preferredDate}
                                    onChange={handleDemoInputChange}
                                    min={new Date().toISOString().split('T')[0]}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                                    Preferred Demo Time
                                </label>
                                <select
                                    name="preferredTime"
                                    value={demoFormData.preferredTime}
                                    onChange={handleDemoInputChange}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box',
                                        backgroundColor: 'white'
                                    }}
                                >
                                    <option value="">Select a time</option>
                                    <option value="9:00 AM">9:00 AM</option>
                                    <option value="10:00 AM">10:00 AM</option>
                                    <option value="11:00 AM">11:00 AM</option>
                                    <option value="2:00 PM">2:00 PM</option>
                                    <option value="3:00 PM">3:00 PM</option>
                                    <option value="4:00 PM">4:00 PM</option>
                                    <option value="5:00 PM">5:00 PM</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                                    What would you like to see?
                                </label>
                                <textarea
                                    name="message"
                                    value={demoFormData.message}
                                    onChange={handleDemoInputChange}
                                    placeholder="Tell us what features interest you most..."
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        resize: 'vertical',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={demoSubmitting}
                                style={{
                                    background: demoSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #3c5bff, #6a29ff)',
                                    color: 'white',
                                    padding: '14px 24px',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    cursor: demoSubmitting ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {demoSubmitting ? 'Submitting...' : 'Book Your Demo'}
                            </button>
                        </form>

                        {/* Footer Info */}
                        <div style={{ marginTop: '20px', padding: '16px', background: '#f9fafb', borderRadius: '8px', textAlign: 'center' }}>
                            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                                🎯 <strong>30-minute personalized demo</strong><br />
                                ⏡ Quick setup • 📊 Live dashboard tour • 🎯 Q&A session
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Demo Success Popup */}
            {demoSuccess && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1001
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '40px',
                        maxWidth: '400px',
                        width: '90%',
                        textAlign: 'center',
                        boxShadow: '0 25px 50px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            background: 'linear-gradient(135deg, #10b981, #34d399)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px',
                            fontSize: '40px'
                        }}>
                            ✓
                        </div>
                        <h2 style={{ margin: '0 0 12px', fontSize: '24px', fontWeight: 700, color: '#1f2937' }}>
                            Thank You!
                        </h2>
                        <p style={{ margin: '0 0 8px', fontSize: '16px', color: '#4b5563' }}>
                            Your demo request has been submitted successfully.
                        </p>
                        <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#6b7280' }}>
                            Our team will contact you shortly to confirm your demo time.
                        </p>
                        <button
                            onClick={() => {
                                setDemoSuccess(false);
                                setShowDemoModal(false);
                            }}
                            style={{
                                background: 'linear-gradient(135deg, #3c5bff, #6a29ff)',
                                color: 'white',
                                padding: '12px 32px',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            Got it!
                        </button>
                    </div>
                </div>
            )}

            <ToastContainer />
        </div>
    );
};

export default AdsPage;
